import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { useContracts, useExpenses, type Expense } from '../../hooks/useQueries'
import { useFormatters } from '../../lib/format'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { SECTION_ICONS } from '../../lib/section-icons'

interface Props {
  org: string
  userUid: string
}

interface ContractGroup {
  contractUid: string | null
  contractName: string
  contractCode?: string | null
  expenses: Expense[]
}

/**
 * "Expenses" section on the user/contact detail page. Lists the expenses this
 * user is the supplier on, grouped by contract. Renders nothing when there
 * are none.
 */
export function UserRelatedExpenses({ org, userUid }: Props) {
  const { t } = useTranslation('expenses')
  const { formatMoney } = useFormatters()

  const { data: expenses = [], isLoading } = useExpenses(org, {
    supplierUserUid: userUid,
    limit: 100,
    enabled: !!userUid,
  })

  // Lightweight fetch (no includes) — only used to resolve contract
  // names/codes for the grouping below, so it stays separate from
  // UserContracts' heavier query above this section.
  const { data: contracts = [] } = useContracts(org, {
    userUid,
    limit: 100,
    enabled: !!userUid,
  })

  // Amounts are stored in integer cents — divide by 100 for display.
  const formatAmount = (cents: number | null | undefined, currency?: string | null) => {
    if (cents == null) return '—'
    return formatMoney(cents, currency)
  }

  const groups = useMemo<ContractGroup[]>(() => {
    const byContract = new Map<string, Expense[]>()
    const noContract: Expense[] = []
    for (const expense of expenses) {
      if (expense.contract_uid) {
        const list = byContract.get(expense.contract_uid) || []
        list.push(expense)
        byContract.set(expense.contract_uid, list)
      } else {
        noContract.push(expense)
      }
    }
    const result: ContractGroup[] = Array.from(byContract.entries()).map(([contractUid, groupExpenses]) => {
      const contract = contracts.find((c) => c.uid === contractUid)
      return {
        contractUid,
        contractName: contract?.name || t('detail.contract_unknown'),
        contractCode: contract?.code,
        expenses: groupExpenses,
      }
    })
    if (noContract.length > 0) {
      result.push({ contractUid: null, contractName: t('related_supplier.no_contract'), expenses: noContract })
    }
    return result
  }, [expenses, contracts, t])

  if (isLoading || expenses.length === 0) return null

  return (
    <Card data-testid="user-related-expenses">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <AppIcon path={SECTION_ICONS.expenses} size="sm" />
          {t('related_supplier.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t(`related_supplier.summary_${expenses.length === 1 ? 'one' : 'other'}` as any, { count: expenses.length })}
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {groups.map((group) => (
          <div
            key={group.contractUid ?? 'no-contract'}
            className="border border-border rounded-lg p-4"
            data-testid={`user-related-expenses-group-${group.contractUid ?? 'none'}`}
          >
            <div className="flex items-center gap-2 mb-2">
              {group.contractUid ? (
                <Link
                  to="/$org/contracts/$contractUid"
                  params={{ org, contractUid: group.contractUid }}
                  className="text-sm font-medium text-foreground hover:underline"
                  data-testid={`user-related-expenses-contract-link-${group.contractUid}`}
                >
                  {group.contractName}
                </Link>
              ) : (
                <span className="text-sm font-medium text-muted-foreground">{group.contractName}</span>
              )}
              {group.contractCode && (
                <span className="text-xs text-muted-foreground">{group.contractCode}</span>
              )}
            </div>
            <div className="space-y-1">
              {group.expenses.map((expense) => (
                <div
                  key={expense.uid}
                  className="flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 border-border/50"
                  data-testid={`user-related-expense-row-${expense.uid}`}
                >
                  <Link
                    to="/$org/expenses/$expenseUid"
                    params={{ org, expenseUid: expense.uid! }}
                    className="text-sm font-medium text-foreground hover:underline flex-shrink min-w-0 truncate"
                    data-testid={`user-related-expense-link-${expense.uid}`}
                  >
                    {expense.label || expense.uid}
                  </Link>
                  <div className="flex-1" />
                  <span className="text-sm font-medium text-foreground flex-shrink-0">
                    {formatAmount(expense.amount, expense.currency)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
